/**
 * Reprise des données depuis la console (ARTO-126, spec 2026-10-11 § 4 et 12) :
 * le mandat de la galerie, puis les lots que l'opérateur y dépose, essaie et
 * lance. `docs/dev/api.md` § 12.13 donne le contrat.
 *
 * Tout ce qui peut encore bouger côté API est isolé ici : le chemin de la
 * relance d'un lot échoué, et la forme des rapports (celui des œuvres a sa
 * propre forme, normalisée en `RapportLot`). Les écrans ne lisent que ces
 * types. En mode `?demo`, aucune requête réseau (`demoReprise.ts`).
 */
import { ApiError, estErreurConfinement, modeDemo, requete } from './api.js';
import { signalerConfinement } from './confinement.js';
import { demoPlan } from './demoPlan.js';
import { demoReprise } from './demoReprise.js';

export const NATURES_LOT = [
  'oeuvres',
  'lieux',
  'artistes',
  'contacts',
  'expositions',
  'factures-historiques',
  'documents',
  'images',
  'localisations',
  'provenance',
  'editions',
  'membres',
] as const;
export type NatureLot = (typeof NATURES_LOT)[number];

/**
 * Les natures qu'un lot reçoit par dépôt de fichiers (tableur et annexes) : les
 * images ne s'envoient pas ainsi, elles se lisent dans un dépôt (ARTO-131).
 */
export type NatureDepot = Exclude<NatureLot, 'images'>;
export const NATURES_DEPOT: readonly NatureDepot[] = NATURES_LOT.filter(
  (nature): nature is NatureDepot => nature !== 'images',
);

export type EtatLot =
  | 'depose'
  | 'profile'
  | 'transforme'
  | 'rapproche'
  | 'essaye'
  | 'pret'
  | 'enfile'
  | 'en_cours'
  | 'termine'
  | 'echoue'
  | 'annule'
  | 'scelle';

/** Un lot suit encore son travail : la fiche le relit toutes les 3 secondes. */
export function lotEnTravail(lot: Pick<LotReprise, 'etat' | 'travail' | 'annulation'>): boolean {
  if (lot.etat === 'enfile' || lot.etat === 'en_cours') return true;
  // Son annulation tourne : demandée, ou en cours (une annulation échouée attend l'opérateur).
  if (lot.annulation?.etat === 'demandee' || lot.annulation?.etat === 'en_cours') return true;
  // Un essai part en fond : le lot reste « déposé » le temps qu'il tourne. Le
  // profilage et la transformation d'un lot tiré d'un dépôt de même.
  return (
    (lot.etat === 'depose' ||
      lot.etat === 'profile' ||
      lot.etat === 'transforme' ||
      lot.etat === 'rapproche') &&
    (lot.travail?.etat === 'en_attente' || lot.travail?.etat === 'en_cours')
  );
}

export interface CompteursLot {
  total: number;
  crees: number;
  completes: number;
  ignores: number;
  refuses: number;
  ecartes: number;
  /** Lot d'images : converties depuis TIFF ou GIF. */
  converties?: number;
}

export interface SourceLot {
  champ: string;
  nom: string;
  octets: number;
  /** Lot tiré d'un dépôt : le fichier du dépôt qu'il cite (ARTO-130). */
  fichierId?: string | null;
}

/** Un morceau de pivot d'un lot transformé : un lot ordinaire, à lancer seul. */
export interface MorceauLot {
  id: string;
  nature: NatureLot;
  rang: number;
  lignes: number;
  etat: EtatLot;
  compteurs: CompteursLot | null;
}

/** Le résumé des morceaux d'un lot transformé (`api.md` § 12.16). */
export interface MorceauxLot {
  morceaux: number;
  etats: Record<string, number>;
  compteurs: CompteursLot;
  pret: boolean;
  liste: MorceauLot[];
}

/**
 * Où en est un travail : ce qu'il a dit au plus toutes les 2 secondes
 * (`api.md` § 12.18). `restantEstimeMs` suit le rythme constaté ; `null` tant
 * qu'il n'a rien fait, ou quand le total est inconnu.
 */
export interface AvancementTravail {
  etape: 'essai' | 'import' | 'annulation' | string;
  faits: number;
  total: number | null;
  maj: string;
  restantEstimeMs: number | null;
}

/** Le travail d'un lot, tel que la fiche le lit. */
export interface TravailLot {
  id: string;
  type: string;
  etat: string;
  tentatives: number;
  maxTentatives: number;
  termineLe: string | null;
  /** Profilage et transformation disent pourquoi ils ont renoncé. */
  erreur?: string | null;
  /** Le code nommé du dernier refus (`ecartement_perime`, `depot_reinventorie`…). */
  raison?: string | null;
  avancement?: AvancementTravail | null;
  /** La file : `basse` pour la reprise. Absent de la fiche d'un lot aujourd'hui. */
  priorite?: 'basse' | 'normale' | 'haute' | null;
}

/** Les lignes refusées à l'essai qu'un opérateur a écartées (arbitrage A4). */
export interface EcartementLot {
  nombre: number;
  motif: string;
  par: string;
  le: string;
}

/** Les compteurs de l'annulation, ceux de l'API. */
export interface CompteursAnnulation {
  supprimes: number;
  conserves: number;
  dejaAbsents: number;
  nonDefaites: number;
}

export type EtatAnnulation = 'demandee' | 'en_cours' | 'faite' | 'echouee';

/** L'annulation d'un lot (`api.md` § 12.18) ; `null` tant qu'elle n'a pas été demandée. */
export interface AnnulationLot {
  etat: EtatAnnulation;
  demandeLe: string;
  par: string;
  faitLe: string | null;
  compteurs: CompteursAnnulation | null;
  travail: TravailLot | null;
}

export interface AnnulableLot {
  possible: boolean;
  raison: string | null;
}

/** Les compteurs de la revue d'un rapprochement d'images, ceux de l'API (`api.md` § 12.17). */
export interface CompteursRapprochement {
  images: number;
  rapproches: number;
  orphelins: number;
  ambigus: number;
  ignores: number;
  nonPrisEnCharge: number;
  /** TIFF et GIF rapprochés, convertis à l'exécution ; absent d'une revue antérieure. */
  aConvertir?: number;
  oeuvresSansImage: number;
  morceaux: number;
}

/** Le chemin d'un lot dans la console : d'où il vient et comment il se mène. */
export type ParcoursLot = 'envoi' | 'correspondance' | 'images' | 'fichiers' | 'morceau';

export interface LotReprise {
  id: string;
  galerieId: string;
  nature: NatureLot;
  etat: EtatLot;
  revision: number;
  sources: SourceLot[];
  options: {
    confidentiels: boolean;
    motifInventaire: string | null;
    /** Lot d'images : le sous-dossier du dépôt lu, `null` pour tout le dépôt. */
    dossier?: string | null;
    /** Lot d'images : la première image remplace l'image principale existante. */
    remplacerPrincipale?: boolean;
  };
  lignes: number;
  compteurs: CompteursLot | null;
  rapportEssai: boolean;
  rapport: boolean;
  /** Le fichier des rejets du dernier essai, par feuille. */
  rejets?: Array<{ feuille: string; lignes: number }>;
  /** Les lignes refusées écartées pour autoriser l'import ; `null` sans écartement. */
  ecartes?: EcartementLot | null;
  travail: TravailLot | null;
  annulation?: AnnulationLot | null;
  /**
   * L'annulation serait-elle refusée ? Dit d'avance, d'après les mêmes refus
   * que la route (`raison` : son code) ; la route reste l'autorité.
   */
  annulable?: AnnulableLot;
  /** Le jour où la reprise a été scellée : le lot ne s'annule plus. */
  scelleLe?: string | null;
  /** D'où vient le lot ; absent : un envoi direct. */
  origine?: 'envoi' | 'depot' | 'morceau';
  /** Comment la fiche le mène ; absent : un lot de correspondance si `origine` est `depot`. */
  parcours?: ParcoursLot;
  depotId?: string | null;
  /** Un morceau cite le lot dont il vient. */
  parentId?: string | null;
  morceau?: { nature: NatureLot; rang: number } | null;
  /** La version exacte du profil de correspondance que le lot utilise. */
  profil?: { id: string; version: number } | null;
  surcharges?: boolean;
  profilage?: { fait: boolean } | null;
  transformation?: { fait: boolean; compteurs: object | null } | null;
  /** Lot d'images : `fait` quand la revue existe, avec ses compteurs. */
  rapprochement?: { fait: boolean; compteurs: CompteursRapprochement | null } | null;
  morceaux?: MorceauxLot | null;
  operateur: string;
  creeLe: string;
  essayeLe: string | null;
  lanceLe: string | null;
  termineLe: string | null;
}

/** Les natures dont le lancement exige un second facteur récent (spec § 4). */
export function exigeSecondFacteur(lot: Pick<LotReprise, 'nature' | 'options'>): boolean {
  return lot.nature === 'factures-historiques' || lot.options.confidentiels;
}

/** L'annulation de ce lot est faite : il ne se relance plus, on dépose un nouveau lot. */
export function lotAnnule(lot: Pick<LotReprise, 'annulation'>): boolean {
  return lot.annulation?.etat === 'faite';
}

export function lotScelle(lot: Pick<LotReprise, 'etat' | 'scelleLe'>): boolean {
  return lot.etat === 'scelle' || (lot.scelleLe ?? null) !== null;
}

/** Les états d'un lot exécuté : ceux d'où l'annulation et le scellement partent. */
export function lotExecute(lot: Pick<LotReprise, 'etat'>): boolean {
  return lot.etat === 'termine' || lot.etat === 'echoue';
}

// ── Mandat ───────────────────────────────────────────────────────────────

export interface MandatVue {
  id: string;
  etat: 'en_attente' | 'actif' | 'refuse' | 'revoque' | 'remplace';
  source: 'contrat' | 'galerie';
  natures: NatureLot[];
  confidentiels: boolean;
  reference: string;
  motif: string;
  dureeJours: number | null;
  demandePar: string | null;
  accordePar: string | null;
  demandeLe: string;
  accordeLe: string | null;
  expireLe: string;
  revoqueLe: string | null;
  revoqueDepuis: 'console' | 'galerie' | null;
}

export interface EtatMandat {
  actif: MandatVue | null;
  demande: MandatVue | null;
}

export interface DemandeMandat {
  natures: NatureLot[];
  confidentiels: boolean;
  motif: string;
  dureeJours: number;
}

export interface PoseMandat {
  natures: NatureLot[];
  confidentiels: boolean;
  /** Jour d'échéance `AAAA-MM-JJ` ; l'API reçoit la fin de cette journée. */
  expireLe: string;
  reference: string;
  motif: string;
}

export const DUREE_DEMANDE_MAX_JOURS = 30;
export const MOTIF_MIN = 8;

// ── Dépôt d'un lot ───────────────────────────────────────────────────────

export type ChampLot = 'fichier' | 'accrochages' | 'pdfs' | 'fichiers' | 'images';

/** Les fichiers d'une nature : le tableur principal, puis ses annexes. */
export interface ChampDeNature {
  champ: ChampLot;
  requis: boolean;
  /** Le type de fichier attendu, pour le sélecteur et le libellé. */
  genre: 'tableur' | 'archive';
}

export const CHAMPS_PAR_NATURE: Readonly<Record<NatureDepot, readonly ChampDeNature[]>> = {
  oeuvres: [
    { champ: 'fichier', requis: true, genre: 'tableur' },
    { champ: 'images', requis: false, genre: 'archive' },
  ],
  lieux: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
  artistes: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
  contacts: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
  expositions: [
    { champ: 'fichier', requis: true, genre: 'tableur' },
    { champ: 'accrochages', requis: false, genre: 'tableur' },
  ],
  'factures-historiques': [
    { champ: 'fichier', requis: true, genre: 'tableur' },
    { champ: 'pdfs', requis: true, genre: 'archive' },
  ],
  documents: [
    { champ: 'fichier', requis: true, genre: 'tableur' },
    { champ: 'fichiers', requis: true, genre: 'archive' },
  ],
  localisations: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
  provenance: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
  editions: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
  membres: [{ champ: 'fichier', requis: true, genre: 'tableur' }],
};

export interface DepotLot {
  nature: NatureDepot;
  fichiers: Partial<Record<ChampLot, File>>;
  /** Documents seulement. */
  confidentiels: boolean;
  /** Œuvres seulement : le motif des numéros d'inventaire à attribuer. */
  motifInventaire: string;
}

/** Il manque un fichier requis ? Le premier champ manquant, sinon `null`. */
export function champManquant(depot: DepotLot): ChampLot | null {
  for (const champ of CHAMPS_PAR_NATURE[depot.nature]) {
    if (champ.requis && !depot.fichiers[champ.champ]) return champ.champ;
  }
  return null;
}

// ── Rapports ─────────────────────────────────────────────────────────────

export interface LigneRapport {
  ligne: number;
  libelle: string;
  messages: string[];
  /** Fichier secondaire concerné (`accrochages`) ; absent : le fichier principal. */
  fichier?: string;
}

/**
 * Le rapport d'un essai ou d'un import, quelle que soit la nature. Les
 * compteurs sont ceux du rapport : `crees + completes + ignores + refuses`
 * vaut `total`.
 */
export interface RapportLot {
  essai: boolean;
  total: number;
  crees: number;
  completes: number;
  ignores: number;
  refuses: number;
  colonnesInconnues: string[];
  erreurs: LigneRapport[];
  avertissements: LigneRapport[];
  /** Compteurs propres à une nature (œuvres accrochées, images déposées…). */
  details: Record<string, number>;
  /** Listes propres à une nature (artistes créés…). */
  listes: Record<string, string[]>;
  /** Le delta : lignes connues par leur référence d'origine et changées depuis, jamais écrasées. */
  revoir: LigneRevoir[];
}

/** Une ligne signalée au delta (`api.md` § 12.18) : rien n'y a été écrasé. */
export interface LigneRevoir {
  ligne: number;
  libelle: string;
  /** Rangée qualifiée par sa source : `manuel:1042`. */
  refOrigine: string;
  cibleId: string;
}

type Objet = Record<string, unknown>;

function objet(valeur: unknown): Objet {
  return typeof valeur === 'object' && valeur !== null ? (valeur as Objet) : {};
}

function nombre(valeur: unknown): number {
  return typeof valeur === 'number' && Number.isFinite(valeur) ? valeur : 0;
}

function textes(valeur: unknown): string[] {
  return Array.isArray(valeur) ? valeur.filter((x): x is string => typeof x === 'string') : [];
}

/** Une ligne du rapport des natures (`libelle`, `messages`) ou des œuvres (`titre`, `erreurs`). */
function ligneDeRapport(brute: unknown): LigneRapport {
  const ligne = objet(brute);
  const libelle = typeof ligne['libelle'] === 'string' ? ligne['libelle'] : ligne['titre'];
  const messages = Array.isArray(ligne['messages']) ? ligne['messages'] : ligne['erreurs'];
  return {
    ligne: nombre(ligne['ligne']),
    libelle: typeof libelle === 'string' ? libelle : '',
    messages: textes(messages),
    ...(typeof ligne['fichier'] === 'string' ? { fichier: ligne['fichier'] } : {}),
  };
}

/**
 * Lit un rapport brut. Les œuvres déposent `RapportImport`
 * (`artistesCrees`, `imagesDeposees` au premier niveau, `titre`/`erreurs` par
 * ligne) ; les autres natures `RapportReprise` (`details`, `listes`,
 * `libelle`/`messages`). Les deux passent ici, et rien d'autre n'est gardé :
 * un champ inconnu du rapport ne s'affiche jamais.
 */
export function normaliserRapport(brut: unknown): RapportLot {
  const rapport = objet(brut);
  const details: Record<string, number> = {};
  for (const [cle, valeur] of Object.entries(objet(rapport['details']))) {
    details[cle] = nombre(valeur);
  }
  for (const cle of ['imagesDeposees', 'imagesNonDeposees']) {
    if (rapport[cle] !== undefined) details[cle] = nombre(rapport[cle]);
  }
  const listes: Record<string, string[]> = {};
  for (const [cle, valeur] of Object.entries(objet(rapport['listes'])))
    listes[cle] = textes(valeur);
  if (rapport['artistesCrees'] !== undefined)
    listes['artistesCrees'] = textes(rapport['artistesCrees']);
  return {
    essai: rapport['essai'] === true,
    total: nombre(rapport['total']),
    crees: nombre(rapport['crees']),
    completes: nombre(rapport['completes']),
    ignores: nombre(rapport['ignores']),
    refuses: nombre(rapport['refuses']),
    colonnesInconnues: textes(rapport['colonnesInconnues']),
    erreurs: Array.isArray(rapport['erreurs']) ? rapport['erreurs'].map(ligneDeRapport) : [],
    avertissements: Array.isArray(rapport['avertissements'])
      ? rapport['avertissements'].map(ligneDeRapport)
      : [],
    details,
    listes,
    revoir: Array.isArray(rapport['revoir']) ? rapport['revoir'].map(ligneARevoir) : [],
  };
}

function ligneARevoir(brute: unknown): LigneRevoir {
  const ligne = objet(brute);
  return {
    ligne: nombre(ligne['ligne']),
    libelle: typeof ligne['libelle'] === 'string' ? ligne['libelle'] : '',
    refOrigine: typeof ligne['refOrigine'] === 'string' ? ligne['refOrigine'] : '',
    cibleId: typeof ligne['cibleId'] === 'string' ? ligne['cibleId'] : '',
  };
}

/** La somme des compteurs vaut-elle le total ? Un rapport qui l'oublie se signale. */
export function compteursCoherents(
  compteurs: Pick<RapportLot, 'total' | 'crees' | 'completes' | 'ignores' | 'refuses'>,
): boolean {
  return (
    compteurs.crees + compteurs.completes + compteurs.ignores + compteurs.refuses ===
    compteurs.total
  );
}

// ── Appels ───────────────────────────────────────────────────────────────

const base = (idGalerie: string): string =>
  `/v1/console/galleries/${encodeURIComponent(idGalerie)}`;

export async function obtenirMandat(idGalerie: string): Promise<EtatMandat> {
  if (modeDemo()) return demoReprise.mandat(idGalerie);
  return requete<EtatMandat>(`${base(idGalerie)}/mandat`);
}

/** Direction de Venio, second facteur récent : `403 mfa_step_up_requis` sinon. */
export async function poserMandat(idGalerie: string, corps: PoseMandat): Promise<MandatVue> {
  if (modeDemo()) return demoReprise.poserMandat(idGalerie, corps);
  const { expireLe, ...reste } = corps;
  return requete<MandatVue>(`${base(idGalerie)}/mandat`, {
    methode: 'PUT',
    corps: { ...reste, expireLe: new Date(`${expireLe}T23:59:59`).toISOString() },
  });
}

export async function demanderMandat(idGalerie: string, corps: DemandeMandat): Promise<MandatVue> {
  if (modeDemo()) return demoReprise.demanderMandat(idGalerie, corps);
  return requete<MandatVue>(`${base(idGalerie)}/mandat/demande`, { methode: 'POST', corps });
}

/** Retire le mandat en cours et les demandes en attente. */
export async function retirerMandat(idGalerie: string): Promise<void> {
  if (modeDemo()) return demoReprise.retirerMandat(idGalerie);
  await requete<void>(`${base(idGalerie)}/mandat`, { methode: 'DELETE' });
}

export async function listerLots(idGalerie: string): Promise<LotReprise[]> {
  if (modeDemo()) return demoPlan.avecAnnulable(idGalerie, demoReprise.lots(idGalerie));
  const { lots } = await requete<{ lots: LotReprise[] }>(`${base(idGalerie)}/reprise/lots`);
  return lots;
}

export async function obtenirLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) {
    const [lot] = demoPlan.avecAnnulable(idGalerie, [demoReprise.lot(idGalerie, lotId)]);
    return lot!;
  }
  return requete<LotReprise>(`${base(idGalerie)}/reprise/lots/${encodeURIComponent(lotId)}`);
}

/** Dépose les fichiers d'un lot (multipart) ; rien n'est écrit chez la galerie. */
export async function deposerLot(idGalerie: string, depot: DepotLot): Promise<LotReprise> {
  if (modeDemo()) return demoReprise.deposer(idGalerie, depot);
  const formulaire = new FormData();
  formulaire.append('nature', depot.nature);
  if (depot.nature === 'documents') {
    formulaire.append('confidentiels', depot.confidentiels ? 'true' : 'false');
  }
  if (depot.nature === 'oeuvres' && depot.motifInventaire.trim() !== '') {
    formulaire.append('motifInventaire', depot.motifInventaire.trim());
  }
  for (const { champ } of CHAMPS_PAR_NATURE[depot.nature]) {
    const fichier = depot.fichiers[champ];
    if (fichier) formulaire.append(champ, fichier);
  }
  const reponse = await fetch(`${base(idGalerie)}/reprise/lots`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Artosera-CSRF': '1' },
    body: formulaire,
  });
  const donnees = (await reponse.json().catch(() => null)) as
    (LotReprise & { code?: string; message?: string; details?: unknown }) | null;
  if (!reponse.ok) {
    const refus = new ApiError(
      reponse.status,
      donnees?.code ?? 'erreur_inconnue',
      donnees?.message ?? 'Une erreur est survenue.',
      donnees?.details ?? null,
    );
    if (estErreurConfinement(refus)) signalerConfinement();
    throw refus;
  }
  return donnees as LotReprise;
}

export async function essayerLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoReprise.essayer(idGalerie, lotId);
  return requete<LotReprise>(
    `${base(idGalerie)}/reprise/lots/${encodeURIComponent(lotId)}/essayer`,
    {
      methode: 'POST',
    },
  );
}

/** `403 mfa_step_up_requis` pour les factures antérieures et les documents confidentiels. */
export async function lancerLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoReprise.lancer(idGalerie, lotId);
  return requete<LotReprise>(
    `${base(idGalerie)}/reprise/lots/${encodeURIComponent(lotId)}/executer`,
    {
      methode: 'POST',
    },
  );
}

/**
 * Relance un lot échoué : la même route que le lancement (`…/executer`), qui
 * revérifie le droit, le mandat et le second facteur, et remet en file le
 * MÊME travail.
 */
export async function relancerLot(idGalerie: string, lotId: string): Promise<LotReprise> {
  if (modeDemo()) return demoReprise.relancer(idGalerie, lotId);
  return lancerLot(idGalerie, lotId);
}

/** Le rapport d'un essai ou celui de l'import, normalisé. */
export async function lireRapport(
  idGalerie: string,
  lotId: string,
  type: 'essai' | 'final',
): Promise<RapportLot> {
  if (modeDemo()) return demoReprise.rapport(idGalerie, lotId, type);
  const brut = await requete<unknown>(
    `${base(idGalerie)}/reprise/lots/${encodeURIComponent(lotId)}/rapport?type=${type}`,
  );
  return normaliserRapport(brut);
}

/** Les gabarits de chaque nature : le principal, et l'annexe des accrochages. */
export type Gabarit = { nature: NatureDepot; feuille?: 'accrochages' };

/** Télécharge un gabarit CSV (session et cookie : un simple lien ne suffit pas en démo). */
export async function telechargerGabarit(idGalerie: string, gabarit: Gabarit): Promise<void> {
  const nomFichier = `gabarit-${gabarit.feuille ?? gabarit.nature}.csv`;
  let blob: Blob;
  if (modeDemo()) {
    blob = new Blob([demoReprise.gabarit(gabarit)], { type: 'text/csv;charset=utf-8' });
  } else {
    const parametre = gabarit.feuille ? `?feuille=${gabarit.feuille}` : '';
    const reponse = await fetch(
      `${base(idGalerie)}/reprise/gabarits/${encodeURIComponent(gabarit.nature)}${parametre}`,
      { credentials: 'include' },
    );
    if (!reponse.ok) {
      const erreur = (await reponse.json().catch(() => null)) as {
        code?: string;
        message?: string;
        details?: unknown;
      } | null;
      throw new ApiError(
        reponse.status,
        erreur?.code ?? 'erreur_inconnue',
        erreur?.message ?? 'Une erreur est survenue.',
        erreur?.details ?? null,
      );
    }
    blob = await reponse.blob();
  }
  const url = URL.createObjectURL(blob);
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nomFichier;
  document.body.append(lien);
  lien.click();
  lien.remove();
  URL.revokeObjectURL(url);
}

// ── Erreurs nommées ──────────────────────────────────────────────────────

/** Les raisons de l'API que l'écran dit en clair (`details.raison`, sinon `code`). */
export const RAISONS_REPRISE = [
  'mandat_absent',
  'nature_hors_mandat',
  'mfa_step_up_requis',
  'lot_non_pret',
  'essai_en_cours',
  'lot_modifie',
  'lot_deja_lance',
  'module_inactif',
  'fichier_requis',
  'archive_requise',
  'depot_purge',
] as const;
export type RaisonReprise = (typeof RAISONS_REPRISE)[number];

export function raisonReprise(erreur: unknown): RaisonReprise | null {
  if (!(erreur instanceof ApiError)) return null;
  const details = erreur.details as { raison?: unknown } | null;
  const candidates = [details?.raison, erreur.code];
  for (const candidate of candidates) {
    const trouvee = RAISONS_REPRISE.find((raison) => raison === candidate);
    if (trouvee) return trouvee;
  }
  return null;
}
